// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useCallback, useMemo } from "react"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { useToast } from "@/hooks/use-toast"
import { WorkspaceBillsTable } from '@/components/workspace-bills/WorkspaceBillsTable'
import { BillDialog } from '@/components/shared/BillDialog'
import { useWorkspaceBillsWorkflow } from '@/hooks/workspace-bills/useWorkspaceBillsWorkflow'
import { useImpactScoreFilter } from '@/hooks/workspace-bills/useImpactScoreFilter'
import { WorkspaceBillsPageHeader } from '@/components/workspace-bills/WorkspaceBillsPageHeader'
import { WorkspaceBillsPageFilters } from '@/components/workspace-bills/WorkspaceBillsPageFilters'
import type { WorkspaceBillAction, WorkspaceBill } from '@/types/workspace-bill'
import type { APISession } from '@/types/session'
import { getAllAPISessions, getLatestAPISession } from '@/types/session'
import { useConfig } from "@/hooks/config/useConfig"
import { getSchemaForType } from "@/utils/schema/schema-type-utils"
import { getFilterScoreRange } from "@/utils/schema/schema-range-utils"
import { useAuth } from '@umd-dit-ai-workspace/workspaces-component';
import { handleBulkPrint } from "@/utils/bulk-print"

export default function WorkspaceBillsPage() {
  const { toast } = useToast()
  const [selected_bill_for_analysis, setSelectedBillForAnalysis] = useState<WorkspaceBill | null>(null)
  const { config } = useConfig()
  const { selectedOrganizationId, selectedWorkspaceId } = useAuth()

  // Get the dynamic filter score range from the schema
  const { min, max } = useMemo(() => {
    const schema = getSchemaForType(config || undefined, 'monitor')
    return getFilterScoreRange(schema)
  }, [config])

  // Use the impact score filter hook with dynamic range and workspace-specific state
  const { 
    slider_value, 
    debounced_min_impact_score, 
    handle_slider_change 
  } = useImpactScoreFilter(min, max, selectedOrganizationId, selectedWorkspaceId)

  // Default to the latest session available
  const initial_session = useMemo(() => {
    return getLatestAPISession()
  }, [])

  const {
    workspace_bills,
    is_loading,
    selected_bills,
    selected_session,
    set_selected_session,
    handle_select_bill,
    handle_select_all,
    process_bill_action,
    current_page,
    page_size,
    total_pages,
    handle_page_change,
    search_query,
    set_search_query,
    processing_actions,
    sort_field,
    sort_direction,
    set_sorting,
    total_filtered_items,
    refresh_bills
  } = useWorkspaceBillsWorkflow(debounced_min_impact_score, initial_session)

  const handle_session_change = useCallback((value: string) => {
    if (getAllAPISessions().includes(value as APISession)) {
      set_selected_session(value as APISession)
    }
  }, [set_selected_session])

  const handle_bill_action = useCallback(async (bill_id: string, action: WorkspaceBillAction, all_bill_ids?: string[]) => {
    try {
      const bills_to_process = all_bill_ids || [bill_id];
      await process_bill_action(bills_to_process, action)
      
      // Only show notification for single bills (bulk actions handled separately)
      if (!all_bill_ids) {
        toast({
          title: `Action '${action}' Started`,
          description: `Processing started for bill ${bill_id}`
        })
      }
    } catch (error) {
      console.error("Error in handle_bill_action:", error);
      toast({
        title: "Action Failed",
        description: "There was an error processing the bill(s).",
        variant: "destructive"
      })
    }
  }, [process_bill_action, toast])

  const handle_bulk_print = useCallback(async () => {
    try {
      if (selected_bills.length === 0) {
        toast({
          title: "No Bills Selected",
          description: "Please select bills to print.",
          variant: "destructive"
        })
        return
      }

      // Get the legislative review schema
      const legislative_review_schema = getSchemaForType(config || undefined, 'legislative_review')
      
      toast({
        title: "Preparing Print",
        description: `Preparing to print ${selected_bills.length} legislative review${selected_bills.length > 1 ? 's' : ''}...`
      })

      await handleBulkPrint(selected_bills, selected_session, legislative_review_schema)
      
      toast({
        title: "Print Ready",
        description: "Print dialog should now be open."
      })
    } catch (error) {
      console.error("Error in bulk print:", error)
      toast({
        title: "Print Failed",
        description: "There was an error preparing the print document.",
        variant: "destructive"
      })
    }
  }, [selected_bills, selected_session, config, toast])

  return (
    <div className="container mx-auto p-2 sm:p-4 md:p-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <WorkspaceBillsPageHeader
              debounced_min_impact_score={debounced_min_impact_score}
              selected_session={selected_session}
              on_session_change={handle_session_change}
              on_refresh={refresh_bills}
              is_loading={is_loading}
            />
            
            <WorkspaceBillsPageFilters
              slider_value={slider_value}
              debounced_min_impact_score={debounced_min_impact_score}
              handle_slider_change={handle_slider_change}
            />
          </div>
        </CardHeader>
        <CardContent>
          <WorkspaceBillsTable
            workspace_bills={workspace_bills}
            selected_bills={selected_bills}
            on_select_bill={handle_select_bill}
            on_select_all={handle_select_all}
            on_process_action={handle_bill_action}
            on_show_impact_details={(bill) => setSelectedBillForAnalysis(bill)}
            current_page={current_page}
            page_size={page_size}
            total_pages={total_pages}
            on_page_change={handle_page_change}
            search_query={search_query}
            on_search_change={set_search_query}
            is_loading={is_loading}
            processing_actions={processing_actions}
            sort_field={sort_field}
            sort_direction={sort_direction}
            on_sort_change={set_sorting}
            session={selected_session}
            total_filtered_items={total_filtered_items}
            on_bulk_print={handle_bulk_print}
          />
        </CardContent>
      </Card>

      <BillDialog
        bill={selected_bill_for_analysis}
        open={!!selected_bill_for_analysis}
        onOpenChange={(open) => !open && setSelectedBillForAnalysis(null)}
      />
    </div>
  )
}
